import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { MessageService } from '@services/util/message.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

/**
 * How many rows /search-data may return per query. The panel has no paging, so
 * this is the hard ceiling on what a user can pick from — too low and matching
 * records are silently unreachable.
 */
const SEARCH_RESULT_LIMIT = 50;

@Component({
  selector: 're-multiselect',
  templateUrl: './re-multiselect.component.html',
  styleUrls: ['./re-multiselect.component.scss'],
})
export class ReMultiselectComponent implements OnInit, OnDestroy {
  private onScrollCapture = () => {
    if (this.isHide && this.escapeOverflow) {
      this.updateFixedPosition();
    }
  };
  @Input() module: any;
  @Input() type: string = '';
  @Input() agent_id: string = null;
  @Input() agency_id: string = null;
  @Input() company_type: string = '';
  @Input() contact_type: string = '';
  @Input() placeholder: any = 'Search Response';
  @Input() selected_ids: any = null;
  @Input() listing_ids: any[] = null;
  @Input() project_ids: any[] = null;
  @Input() isCreateNew: boolean = false;
  @Input() isMultiple: boolean = true;
  @Input() isFullObject: boolean = false;
  @Input() isEmail: boolean = false;
  /**
   * Hide search results with no email on file. Off by default — most callers (SMS flows, generic
   * pickers) have no reason to care — opt in where the picked record is only useful with an
   * email, e.g. sending something that can only be emailed.
   *
   * Search-only: a record already hydrated in from `selected_ids` is never dropped by this, so an
   * existing selection made before this filter existed still displays correctly.
   */
  @Input() requireEmail: boolean = false;
  @Input() isClear: boolean = false;
  @Input() isMessage: boolean = false;
  @Input() isViewDropdown: boolean = false;
  // When true, render the dropdown panel with position:fixed anchored to the
  // trigger. Fixed-position elements ignore ancestor overflow, so the panel is
  // not clipped when the component sits inside an overflow:auto/hidden container
  // (e.g. the contact editor's p-splitter panel).
  @Input() escapeOverflow: boolean = false;

  @Input() truncate: number = 30;
  @Input() height: number = null;
  @Input() borderRadius: number = null;
  @Input() style: any = {};
  @Input() required_class: any;

  @Output() onUpdate = new EventEmitter<any>();
  @Output() onAddRemove = new EventEmitter<any>();
  @Output() createNew = new EventEmitter<any>();
  @Output() editRecord = new EventEmitter<any>();

  input$ = new Subject<string>();
  @ViewChild('myInput') myInputElement!: ElementRef;
  @ViewChild('boxRef') boxRef!: ElementRef;
  fixedStyle: any = null;
  result_list = [];
  selected = [];
  isAllSelected: boolean = false;
  isHide: boolean = false;
  isHide1: boolean = true;
  private lastSelectedIds: any = null;

  constructor(
    private api: ApiService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selected_ids'] && !this.isHide) {
      const current = JSON.stringify(this.selected_ids);

      const currentChange = changes['selected_ids'].currentValue;
      const previousChange = changes['selected_ids'].previousValue;
      if (previousChange && previousChange?.includes(currentChange?._id)) return;

      if (this.lastSelectedIds === current) return;
      this.lastSelectedIds = current;
      if (this.selected_ids?.length > 0 && this.isMultiple) {
        this.api
          .get(
            // Resolving an already-chosen id into display data — not a candidate search, so it
            // must not carry agent_id/agency_id: those scope what's *searchable*, and a
            // just-picked record (e.g. a contact not yet linked to that agency) can legitimately
            // fail that filter, which used to make the chip render empty despite a real selection.
            `/search-data?office_id=${this.api.getOfficeId()}&exclude_solds=true&filter=${JSON.stringify(this.selected_ids)}&Keywords=&module=${this.module}&type=${this.type}&contact_type=${this.contact_type}&company_type=${this.company_type}&page=1&limit=40&project_id=${JSON.stringify(this.project_ids)}&listing_id=${JSON.stringify(this.listing_ids)}`
          )
          .subscribe({
            next: response => {
              this.selected = this.prepareResult(response.data, this.getField());
              this.selected.map(f => (f['isCheck'] = true));
              this.onUpdate.emit(this.selected);
            },
          });
      } else if (this.selected_ids && !this.isMultiple) {
        this.api
          .get(
            // Same reasoning as above — no agent_id/agency_id on the resolve-by-id lookup.
            `/search-data?office_id=${this.api.getOfficeId()}&exclude_solds=true&filter=${JSON.stringify([this.selected_ids])}&Keywords=&module=${this.module}&type=${this.type}&contact_type=${this.contact_type}&company_type=${this.company_type}&page=1&limit=40&project_id=${JSON.stringify(this.project_ids)}&listing_id=${JSON.stringify(this.listing_ids)}`
          )
          .subscribe({
            next: res => {
              this.selected = this.prepareResult(res.data, this.getField());
              this.selected.map(f => (f['isCheck'] = true));
              this.onUpdate.emit(this.selected[0]);
              return false;
            },
          });
      } else if (changes['selected_ids'].currentValue?.length == 0 && changes['selected_ids'].previousValue?.length > 0 && !this.isHide) {
        this.selected = [];
        this.result_list = [];
        this.onSearch();
        this.onUpdate.emit(this.selected);
      }
    } else if (changes['selected_ids'].currentValue?.length == 0 && changes['selected_ids'].previousValue?.length > 0) {
      this.selected = [];
      this.result_list = [];
      this.onSearch();
      this.onUpdate.emit(this.selected);
    }
  }

  ngOnInit() {
    if (this.escapeOverflow) {
      // capture phase so inner scroll containers (which don't bubble scroll) reposition the panel
      window.addEventListener('scroll', this.onScrollCapture, true);
    }
    this.onSearch();
    this.messageService.getMessage().subscribe({
      next: f => {
        if (f?.isClearItems) {
          this.isAllSelected = false;
          this.selected = [];
          this.selected_ids = null;
        }
      },
    });
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onScrollCapture, true);
  }

  trackById(item: any): string {
    return item._id;
  }

  onSearch() {
    this.input$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap(term => this.api.get(this.buildSearchUrl(term)))
      )
      .subscribe(res => this.applyResults(res));
  }

  private buildSearchUrl(term: string): string {
    return `/search-data?office_id=${this.api.getOfficeId()}&exclude_solds=true&Keywords=${encodeURIComponent(term ?? '')}&module=${this.module}&type=${this.type}&contact_type=${this.contact_type}&company_type=${this.company_type}&page=1&limit=${SEARCH_RESULT_LIMIT}&project_id=${JSON.stringify(this.project_ids)}&listing_id=${JSON.stringify(this.listing_ids)}&agent_id=${this.agent_id}&agency_id=${this.agency_id}`;
  }

  private applyResults(res: any) {
    this.result_list = this.prepareResult(res.data, this.getField())
      .filter(f => !this.isViewDropdown || f?._id !== this.api.getUserId())
      .filter(f => !this.requireEmail || !!f.email);
  }

  // Called directly on every dropdown open, bypassing the input$/distinctUntilChanged
  // pipeline — that pipeline dedupes identical consecutive terms (e.g. two opens in a row
  // both sending ' '), which silently swallowed the re-fetch on reopen.
  private refreshOnOpen() {
    this.api.get(this.buildSearchUrl(' ')).subscribe(res => this.applyResults(res));
  }

  prepareResult = (items: any[], field: string) => {
    let f = [];
    const seen = new Set<string>();
    (items || []).forEach(c => {
      // The same _id arriving twice would render a duplicate row and break
      // trackById. Distinct ids with the same name are real records — keep them.
      if (c?._id && seen.has(c._id)) return;
      if (c?._id) seen.add(c._id);
      f.push({
        _id: c._id,
        isCheck: this.isAllSelected ? true : false,
        name: c[field],
        type: c?.type ? c.type : null,
        email: c?.email ? c.email : null,
        mobile_no: c?.mobile_no ? c.mobile_no : null,
      });
    });
    return f;
  };

  onInputChange(value: string) {
    this.input$.next(value); // Emits the input value
  }

  getField = () => {
    if (['users', 'contacts', 'contract', 'leads'].includes(this.module)) {
      return 'full_name';
    }

    if (['listings', 'property'].includes(this.module)) {
      return 'property_address';
    }

    if (['projects'].includes(this.module)) {
      return 'Project_Name';
    }

    if (['unit', 'eois'].includes(this.module)) {
      return 'lot_name';
    }

    if (['tasks'].includes(this.module)) {
      return 'title';
    }

    return 'name';
  };

  getStyle = (style: any) => {
    let newStyle: any = { ...style };
    if (!style?.maxWidth) {
      // style.maxWidth = '200px';
    }
    return newStyle;
  };

  addRemove = (item: any) => {
    // 🟢 Single select mode — purane selected item ko wapas list mein add karein
    if (!this.isMultiple && this.selected.length > 0) {
      const previousSelected = this.selected[0];

      // Reset its isCheck state
      if (previousSelected) {
        previousSelected.isCheck = false;
      }

      // Wapas result_list mein push karein agar pehle se nahi hai
      const exists = this.result_list.some(r => r._id === previousSelected._id);
      if (!exists) {
        this.result_list.push(previousSelected);
        this.result_list.sort((a, b) => a.name.localeCompare(b.name));
      }

      this.selected = [];
    }

    this.isHide1 = false;
    item.isCheck = !item.isCheck;

    if (item.isCheck) {
      // Add item to selected list if not already present
      if (!this.selected.some(selectedItem => selectedItem._id === item._id)) {
        this.selected.push(item);

        // Remove from result_list
        const index = this.result_list.findIndex(selectedItem => selectedItem._id === item._id);
        if (index !== -1) {
          this.result_list.splice(index, 1);
        }
      }
    } else {
      // Remove item from selected list
      const index = this.selected.findIndex(selectedItem => selectedItem._id === item._id);
      if (index !== -1) {
        this.selected.splice(index, 1);
      }

      // Add item back to result_list and sort it
      const exists = this.result_list.some(r => r._id === item._id);
      if (!exists) {
        this.result_list.push(item);
        this.result_list.sort((a, b) => a.name.localeCompare(b.name));
      }
    }

    setTimeout(() => {
      this.isHide1 = true;
    }, 10);

    this.onUpdate.emit(this.isMultiple ? this.selected : this.isFullObject ? this.selected[0] : this.selected[0]?._id);
    this.onAddRemove.emit(this.isMultiple ? this.selected : this.isFullObject ? this.selected[0] : this.selected[0]?._id);

    if (this.isMessage) {
      this.messageService.sendMessage({
        isRefreshUserReport: true,
      });
    }

    // Single select: one pick is the whole answer, so close the panel.
    // `isHide` is inverted — true means the panel is rendered.
    if (!this.isMultiple) {
      this.isHide = false;
      this.fixedStyle = null;
    }
  };

  ocClickOut = (eve: any) => {
    if (eve?.bol === false && this.isHide1) {
      this.isHide = false;
    }
  };

  showDropdown = () => {
    this.isHide = !this.isHide;
    if (this.isHide) {
      this.updateFixedPosition();
      setTimeout(() => {
        this.myInputElement.nativeElement.focus();
      }, 10);
      // Re-fetch every time the panel opens, not just when result_list is
      // empty — outside-click close leaves result_list populated, so a
      // stale, cached list would otherwise show on reopen.
      this.refreshOnOpen();
    } else {
      this.fixedStyle = null;
    }
  };

  // Anchor the fixed-position panel right under the trigger box so it isn't
  // clipped by any overflow:auto/hidden ancestor.
  private updateFixedPosition() {
    if (!this.escapeOverflow || !this.boxRef?.nativeElement) return;
    const rect = this.boxRef.nativeElement.getBoundingClientRect();
    this.fixedStyle = {
      position: 'fixed',
      top: rect.bottom + 'px',
      left: rect.left + 'px',
      width: rect.width + 'px',
    };
  }

  @HostListener('window:resize')
  @HostListener('window:scroll', ['$event'])
  onViewportChange() {
    if (this.isHide && this.escapeOverflow) {
      this.updateFixedPosition();
    }
  }

  selectAll = () => {
    if (this.isAllSelected) {
      this.result_list.forEach(f => (f.isCheck = true));
      this.selected = [...this.result_list];
      this.onUpdate.emit([{ id_: 'all' }]);
    } else {
      this.result_list.forEach(f => (f.isCheck = false));
      this.selected.forEach(f => (f.isCheck = false));
      this.onUpdate.emit(null);
    }
    if (this.isMessage) {
      this.messageService.sendMessage({
        isRefreshUserReport: true,
      });
    }
  };

  createNewItem = () => {
    this.isHide = false;
    this.createNew.emit(true);
  };

  spliceItem(i) {
    this.selected.splice(i, 1);
    if (this.isClear) {
      this.onUpdate.emit(null);
      this.onAddRemove.emit(null);
    } else {
      const payload = this.isMultiple ? this.selected : this.isFullObject ? this.selected[0] : this.selected[0]?._id;
      this.onUpdate.emit(payload);
      this.onAddRemove.emit(payload);
    }

    if (this.isMessage) {
      this.messageService.sendMessage({
        isRefreshUserReport: true,
      });
    }
  }

  openRecord(record) {
    this.editRecord.emit(record);
  }

  getUniqueItems(list: any[], selected: any[]): any[] {
    if (!list || list.length === 0) return [];
    if (!selected || selected.length === 0) return list;

    return list.filter(item => !selected.some(s => s._id === item._id));
  }
}
